import { useTranslation } from "react-i18next";
import MultiLineChart from "../pages/MultiLineChart";
import { addDays } from "date-fns";
import { useContext, useEffect, useState } from "react";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import Select from "react-select";
import {Consts} from "../pages/consts";

const generateIntakePoints = (medication, intervalStart, intervalEnd) => {
  const startDate = parseDate(medication?.startDate);

  const intakes = medication?.intakes?.map((intake) => {
    const [hours, minutes] = intake?.intakeTime?.split(":")?.map(Number);
    return { ...intake, hours, minutes };
  });

  let currentDate = parseDate(intervalStart);
  const points = [];

  while (currentDate <= intervalEnd) {
    if (currentDate >= startDate) {
      intakes?.forEach((intake) => {
        const intakeDate = parseDate(currentDate);
        intakeDate?.setHours(intake?.hours, intake?.minutes);

        points?.push({
          x: intakeDate,
          y: 0.1, //maxY / 10.0,
          value: intake?.dosage,
          unit: intake?.dosageUnit,
          label: medication?.productName,
          connectPoints: false,
          highlighted: true,
        });
      });
    }

    currentDate = addDays(currentDate, 1);
  }

  return points;
};

const parseDate = (dateString) => {
  if (!dateString) {
    return new Date();
  }

  if (dateString instanceof Date) {
    return new Date(dateString);
  }

  if (typeof dateString !== "string") {
    throw new Error("dateString must be a string");
  }

  const hasTime = dateString?.includes(":");
  const dateParts = dateString?.split(" ")[0]?.split("/");
  const day = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1;
  const year = parseInt(dateParts[2], 10);
  let hours = 0;
  let minutes = 0;

  if (hasTime) {
    const timeParts = dateString?.split(" ")[1]?.split(":");
    hours = parseInt(timeParts[0], 10);
    minutes = parseInt(timeParts[1], 10);
  }

  return new Date(year, month, day, hours, minutes);
};

const transformMedicationsToDataSets = (
  medications,
  intervalStart,
  intervalEnd,
) => {
  if (!medications) {
    return [];
  }

  const points = medications?.flatMap((medication) =>
    generateIntakePoints(medication, intervalStart, intervalEnd),
  );

  return points?.reduce((acc, point) => {
    const existing = acc?.find((item) => item?.label?.toLowerCase() === point?.label?.toLowerCase());

    if (existing) {
      existing?.data?.push({
        x: point?.x,
        y: point?.y,
        value: point?.value,
        unit: point?.unit,
      });
    } else {
      acc?.push({
        label: point?.label,
        data: [
          {
            x: point?.x,
            y: point?.y,
            value: point?.value,
            unit: point?.unit,
          },
        ],
        connectPoints: false,
        highlighted: true,
      });
    }

    return acc;
  }, []);
};

const getIntervalBounds = (dataSets) => {
  if (dataSets?.length === 0) {
    return [new Date(), new Date(), 0];
  }

  const result = dataSets?.reduce(
    (acc, item) => {
      item?.data?.forEach((point) => {
        const xValue = parseDate(point?.x);
        const yValue = point?.y;

        if (acc?.minX === null) {
          acc.minX = new Date(xValue);
        }

        if (acc?.maxX === null) {
          acc.maxX = new Date(xValue);
        }

        if (xValue < acc?.minX) {
          acc.minX = new Date(xValue);
        } else if (xValue > acc?.maxX) {
          acc.maxX = new Date(xValue);
        }

        acc.maxY = Math.max(acc?.maxY, yValue);
      });

      return acc;
    },
    { minX: null, maxX: null, maxY: null },
  );

  result.minX = new Date(result?.minX);
  result.minX?.setDate(result?.minX?.getDate() - 1);
  result.maxX = new Date(result?.maxX);
  result.maxX?.setDate(result?.maxX?.getDate() + 1);
  return [result?.minX, result?.maxX, result?.maxY];
};

const convertDates = (values) => {
  return values?.map((item) => {
    return {
      ...item,
      data: item?.data?.map((dataPoint) => ({
        ...dataPoint,
        x: parseDate(dataPoint?.x),
      })),
    };
  });
};

export const getTranslatedLabel = (label, t) => {
  const labelParts = label?.split("+")?.map((part, i) =>
    i === 0 && label?.includes("plasma_concentration_test") ? t(part?.trim()) : part.trim());

  if (labelParts?.length === 3) {
    labelParts?.push(t("hours"));
  }

  return labelParts?.join(" ");
};

const DiaryValuesLineChart = ({
  header,
  values,
  maxYScale,
  patientId,
  onCloseDialog,
  showDialog,
}) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);
  const [showIntakes, setShowIntakes] = useState(true);
  const [selectedDataSet, setSelectedDataSet] = useState(values[0]?.label);
  const { loading, error, clearError, getDbDrugs } = useMmcService();
  const [patDrugs, setPatDrugs] = useState([]);

  useEffect(() => {
    const fetchPrescriptionsWithIntakes = async () => {
      clearError();

      const drugs = await getDbDrugs(
        context?.token,
        patientId ? patientId : context?.patient?.id,
        false,
        true,
      );

      setPatDrugs(drugs);
    };

    fetchPrescriptionsWithIntakes().then();
  }, [patDrugs?.length]);

  const updatedValues = convertDates(values);

  // check if datasets has units
  const hasUnitData = updatedValues.some((item) => item?.data?.some((dataPoint) => dataPoint?.unit));

  const [intervalStart, intervalEnd, maxY] = getIntervalBounds(
    hasUnitData
      ? updatedValues?.filter((item) => item?.label === selectedDataSet)
      : updatedValues,
  );

  const medicationsDataSets = transformMedicationsToDataSets(
    patDrugs,
    intervalStart,
    intervalEnd,
    maxYScale ? maxYScale : maxY,
  );

  const errorMessage = error && (
    <ErrorMessage message={t("error_by_retrieving_prescribed_drugs")} />
  );

  const spinner = loading && <Spinner />;

  const getUnitLabel = () => {
    const selectedData = updatedValues?.find((item) => item?.label === selectedDataSet);

    if (selectedData) {
      const units = selectedData?.data?.map((dataPoint) => dataPoint?.unit);
      const uniqueUnits = [...new Set(units)];

      return uniqueUnits?.length === 1
        ? uniqueUnits[0]
        : uniqueUnits?.join(", ");
    }

    return "";
  };

  const parameterOptions = values?.map((item) => {
    return {
      value: item?.label,
      label: getTranslatedLabel(item?.label, t),
    };
  });

  return (
    <div className="w3-modal" style={{ display: showDialog ? Consts.BLOCK : Consts.NONE }}>
      <div className="w3-modal-content w3-card-4 w3-animate-zoom w3-theme-l3 dialog-medium">
        <header className="w3-container w3-padding-small w3-center">
          <p>{header}</p>
        </header>
        {errorMessage}
        {spinner}
        <div className="w3-container w3-padding-small">
          {(parameterOptions.length > 1 && hasUnitData) && (
            <div className="w3-row w3-padding-small">
              <div className="w3-quarter">
                <label htmlFor="dataSetSelect">{t("select_dataset")}:</label>
              </div>
              <div className="w3-half">
                <Select
                  id="dataSetSelect"
                  options={parameterOptions}
                  value={parameterOptions?.find((option) => option?.value === selectedDataSet)}
                  onChange={(selectedOption) => setSelectedDataSet(selectedOption?.value)}
                  placeholder={t("select")}
                  className="custom-select"
                  classNamePrefix="custom-select"
                />
              </div>
            </div>
          )}
          {medicationsDataSets?.length > 0 && (
            <div className="w3-container w3-padding-small">
              <label>
                <input
                  type="checkbox"
                  checked={showIntakes}
                  onChange={() => setShowIntakes(!showIntakes)}
                />{" "}
                {t("show_medication_intakes")}
              </label>
            </div>
          )}
          <MultiLineChart
            dataSets={
              hasUnitData
                ? [
                    ...updatedValues?.filter((item) => item?.label === selectedDataSet),
                    ...(showIntakes ? medicationsDataSets : []),
                  ]
                : [
                    ...updatedValues,
                    ...(showIntakes ? medicationsDataSets : []),
                  ]
            }
            xLabel={t("date")}
            yLabel={getUnitLabel()}
            integerYAxis={true}
            maxYValue={maxYScale}
          />
        </div>
        <footer className="w3-container w3-padding-small">
          <p className="w3-right">
            <button
              className="w3-btn w3-round-xlarge w3-theme-d5 w3-margin-left"
              onClick={() => onCloseDialog()}
            >
              {t("close")}
            </button>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default DiaryValuesLineChart;
